import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Navigation, Heart, Shield, Clock, Users, Car, 
  CheckCircle, Star, ArrowRight, Phone, MapPin,
  Accessibility, Calendar, FileText, Zap, Globe,
  Award, TrendingUp, Smartphone, HeartHandshake,
  MessageSquare, Sparkles, Send, X
} from "lucide-react";

export default function SalesPitchPage() {
  const navigate = useNavigate();
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [showPricing, setShowPricing] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [chatMessage, setChatMessage] = useState("");
  const [chatMessages, setChatMessages] = useState([
    { from: "agent", text: "Hi! I'm Sarah from There You Are. How can I help you today?" }
  ]);

  const sendChatMessage = () => {
    if (!chatMessage.trim()) return;
    setChatMessages([...chatMessages, { from: "user", text: chatMessage }]);
    setChatMessage("");
    // Simulate agent response
    setTimeout(() => {
      setChatMessages(prev => [...prev, { 
        from: "agent", 
        text: "Thanks for your message! One of our team members will respond shortly. In the meantime, feel free to start your 14-day free trial or call us at 1-800-THERE-YA." 
      }]);
    }, 1500);
  };

  const testimonials = [
    {
      quote: "Before There You Are, coordinating rides for our dialysis patients was a nightmare. Now it takes seconds, and our patients never miss an appointment.",
      author: "Sarah Mitchell",
      role: "Operations Director, Metro Dialysis Center",
      rating: 5
    },
    {
      quote: "My drivers love this app. They know exactly what equipment each patient needs before they even arrive. It's made our service so much more professional.",
      author: "Marcus Johnson",
      role: "Owner, City Care Transport",
      rating: 5
    },
    {
      quote: "As a wheelchair user, I was always anxious about rides. Now I know my driver is prepared for me. That peace of mind is priceless.",
      author: "Eleanor Thompson",
      role: "Patient",
      rating: 5
    }
  ];

  const stats = [
    { value: "500+", label: "Healthcare Partners" },
    { value: "50,000+", label: "Rides Completed" },
    { value: "99.2%", label: "On-Time Rate" },
    { value: "4.9/5", label: "Patient Satisfaction" }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-4 py-20">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000" />
          <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl animate-pulse delay-500" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text Content */}
            <div className="text-center md:text-left">
              {/* Logo */}
              <div className="flex items-center justify-center md:justify-start gap-3 mb-4 md:mb-8">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-cyan-400 via-purple-500 to-orange-400 rounded-2xl flex items-center justify-center shadow-2xl shadow-cyan-500/30">
                  <Navigation className="w-6 h-6 md:w-8 md:h-8 text-white" />
                </div>
              </div>

              <Badge className="mb-4 md:mb-6 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 border-cyan-400/30 text-cyan-300 px-3 py-1 md:px-4 md:py-2 text-xs md:text-sm">
                <Heart className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2 inline" /> Trusted by 500+ Healthcare Providers
              </Badge>

              <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-black mb-3 md:mb-6 leading-tight drop-shadow-2xl">
                <span className="bg-gradient-to-r from-cyan-300 via-white to-purple-300 bg-clip-text text-transparent animate-pulse-slow">
                  There You Are
                </span>
              </h1>

              <p className="text-xl md:text-2xl lg:text-3xl text-slate-300 mb-2 md:mb-4 font-light">
                Every Patient Deserves to{" "}
                <span className="text-white font-semibold">Arrive with Dignity</span>
              </p>

              <p className="text-sm md:text-lg text-slate-400 max-w-xl mb-4 md:mb-10">
                The only NEMT platform built from the heart. Because getting to a medical appointment 
                shouldn't be harder than the appointment itself.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center md:justify-start mb-4 md:mb-6">
                <Button 
                  size="lg"
                  className="bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500 text-white font-bold px-6 py-4 md:px-8 md:py-6 text-base md:text-lg rounded-xl shadow-2xl shadow-purple-500/30"
                  onClick={() => navigate("/free-trial")}
                  data-testid="sales-start-trial-btn"
                >
                  Start Free Trial <ArrowRight className="ml-2 w-4 h-4 md:w-5 md:h-5" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-slate-600 text-white hover:bg-slate-800 px-6 py-4 md:px-8 md:py-6 text-base md:text-lg rounded-xl"
                  onClick={() => navigate("/")}
                >
                  Watch Demo
                </Button>
                <Button 
                  size="lg"
                  className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white font-bold px-6 py-4 md:px-8 md:py-6 text-base md:text-lg rounded-xl shadow-2xl shadow-green-500/30"
                  onClick={() => setShowPricing(true)}
                >
                  Pricing
                </Button>
              </div>
              
              {/* 14-Day Free Trial Badge */}
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 md:gap-4 mb-4 md:mb-6">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-400/40 rounded-full px-4 py-2 md:px-5">
                  <Sparkles className="w-4 h-4 md:w-5 md:h-5 text-amber-400" />
                  <span className="text-amber-300 font-bold text-sm md:text-lg">14-Day Free Trial</span>
                  <span className="text-slate-400 text-xs md:text-base hidden sm:inline">• No credit card required</span>
                </div>
              </div>

              {/* Chat with Agent Button */}
              <div 
                className="inline-flex items-center gap-3 bg-slate-800/80 border border-slate-600 rounded-xl px-4 py-2 md:px-5 md:py-3 cursor-pointer hover:bg-slate-700/80 hover:border-cyan-500/50 transition-all group mx-auto md:mx-0"
                onClick={() => setShowChat(true)}
              >
                <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center">
                  <MessageSquare className="w-4 h-4 md:w-5 md:h-5 text-white" />
                </div>
                <div className="text-left">
                  <p className="text-white font-semibold text-sm md:text-base">Talk to an Agent</p>
                  <p className="text-slate-400 text-xs md:text-sm">Chat with us now</p>
                </div>
                <div className="w-2 h-2 md:w-3 md:h-3 bg-green-500 rounded-full animate-pulse" />
              </div>
            </div>

            {/* Right Column - Woman with Phone Image */}
            <div className="relative flex justify-center md:justify-end">
              <div className="relative">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/5b1de827-3f18-4164-bcd3-2ea2a97414b6/images/2af4fd8dd9a0148a36b7746ebc1b026538197b15f8fb1cda683ccce317ef1459.png"
                  alt="Woman smiling while using There You Are app on her phone"
                  className="rounded-3xl shadow-2xl max-w-sm md:max-w-md object-cover"
                />
                {/* Decorative glow */}
                <div className="absolute -inset-4 bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-orange-500/20 rounded-3xl blur-2xl -z-10" />
              </div>
            </div>
          </div>

          {/* Stats - Below both columns */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto mt-16">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-3xl md:text-4xl font-black bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
                  {stat.value}
                </p>
                <p className="text-slate-400 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-slate-600 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-cyan-400 rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-24 px-4 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-red-500/20 border-red-400/30 text-red-300">
              The Hidden Healthcare Crisis
            </Badge>
            <h2 className="text-4xl md:text-5xl font-black mb-6">
              3.6 Million Americans{" "}
              <span className="text-red-400">Miss Medical Care</span>{" "}
              Every Year
            </h2>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto">
              Due to transportation barriers. That's not just a statistic—that's grandmothers missing 
              chemotherapy, veterans skipping dialysis, children missing therapy sessions.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-slate-800/50 border-slate-700 hover:border-red-500/50 transition-all">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-red-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Clock className="w-8 h-8 text-red-400" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-white">Missed Appointments</h3>
                <p className="text-slate-400">
                  Every missed ride means a missed treatment, a delayed diagnosis, a worried family.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700 hover:border-orange-500/50 transition-all">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-orange-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Accessibility className="w-8 h-8 text-orange-400" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-white">Unprepared Drivers</h3>
                <p className="text-slate-400">
                  Drivers arrive not knowing the client needs help with their mobility aid—wheelchair, cane, walker, or crutches. No one should be surprised at pickup.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700 hover:border-yellow-500/50 transition-all">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-yellow-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <FileText className="w-8 h-8 text-yellow-400" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-white">Paper Chaos</h3>
                <p className="text-slate-400">
                  Dispatchers juggling spreadsheets, sticky notes, and phone calls. One mistake costs a life.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-24 px-4 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 border-cyan-400/30 text-cyan-300">
              The Solution
            </Badge>
            <h2 className="text-4xl md:text-5xl font-black mb-6">
              One Platform.{" "}
              <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
                Three Heroes.
              </span>{" "}
              Zero Missed Rides.
            </h2>
          </div>

          {/* Three Pillars */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {/* Dispatch */}
            <div className="group">
              <div className="bg-gradient-to-br from-cyan-500/10 to-cyan-500/5 border border-cyan-500/20 rounded-3xl p-8 h-full hover:border-cyan-400/50 transition-all hover:shadow-2xl hover:shadow-cyan-500/10">
                <div className="w-20 h-20 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Users className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-cyan-300">Dispatch Center</h3>
                <p className="text-slate-400 mb-6">
                  Command your fleet with confidence. See every vehicle, every patient, every need—in real time.
                </p>
                <ul className="space-y-3">
                  {["Live fleet tracking", "One-click scheduling", "Batch ride imports", "HIPAA-secure records"].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-300">
                      <CheckCircle className="w-5 h-5 text-cyan-400" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Driver */}
            <div className="group">
              <div className="bg-gradient-to-br from-purple-500/10 to-purple-500/5 border border-purple-500/20 rounded-3xl p-8 h-full hover:border-purple-400/50 transition-all hover:shadow-2xl hover:shadow-purple-500/10">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Car className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-purple-300">Driver App</h3>
                <p className="text-slate-400 mb-6">
                  Everything drivers need, nothing they don't. Know your patient before you arrive.
                </p>
                <ul className="space-y-3">
                  {["Patient mobility needs", "One-tap navigation", "Client PIN verification", "Real-time updates"].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-300">
                      <CheckCircle className="w-5 h-5 text-purple-400" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Client */}
            <div className="group">
              <div className="bg-gradient-to-br from-orange-500/10 to-orange-500/5 border border-orange-500/20 rounded-3xl p-8 h-full hover:border-orange-400/50 transition-all hover:shadow-2xl hover:shadow-orange-500/10">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-orange-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <HeartHandshake className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-orange-300">Patient Portal</h3>
                <p className="text-slate-400 mb-6">
                  Peace of mind for patients and families. Know your ride is coming, prepared just for you.
                </p>
                <ul className="space-y-3">
                  {["Track your driver", "See vehicle details", "Accessibility confirmed", "Family notifications"].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-300">
                      <CheckCircle className="w-5 h-5 text-orange-400" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Accessibility Feature Highlight */}
      <section className="py-24 px-4 bg-gradient-to-r from-amber-500/10 via-slate-900 to-amber-500/10">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-amber-500/20 border-amber-400/30 text-amber-300">
                <Accessibility className="w-4 h-4 mr-2 inline" /> Built for Accessibility
              </Badge>
              <h2 className="text-4xl font-black mb-6">
                "Client Has Mobility Aid:{" "}
                <span className="text-amber-400">Wheelchair</span>"
              </h2>
              <p className="text-xl text-slate-300 mb-6">
                Those five words change everything. When a driver knows before arriving that a 
                patient uses a wheelchair, needs oxygen, or requires help in and out of the vehicle—
                <span className="text-white font-semibold"> dignity is preserved</span>.
              </p>
              <p className="text-slate-400 mb-8">
                Our system ensures every accessibility need flows from dispatcher to driver 
                automatically. No missed details. No awkward surprises. Just prepared, compassionate care.
              </p>
              <div className="flex flex-wrap gap-3">
                {["Wheelchair", "Rolling Walker", "Cane", "Oxygen", "Service Animal", "Step Stool"].map((item, i) => (
                  <Badge key={i} className="bg-amber-500/20 text-amber-300 border-amber-500/30">
                    {item}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="bg-slate-800 rounded-3xl p-6 border border-slate-700 shadow-2xl">
                <div className="bg-slate-900 rounded-2xl p-4 mb-4">
                  <p className="text-slate-400 text-sm mb-2">Driver View - Eleanor Thompson</p>
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                    <p className="text-amber-300">
                      <Accessibility className="w-4 h-4 inline mr-2" />
                      <span className="font-semibold">Client has mobility aid:</span> Wheelchair
                    </p>
                    <p className="text-blue-300 mt-2">
                      <span className="font-semibold">Client Needs:</span> Help In/Out of Vehicle
                    </p>
                  </div>
                </div>
                <p className="text-center text-slate-500 text-sm">
                  Drivers see this before every pickup
                </p>
              </div>
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-amber-500/20 rounded-full blur-2xl" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-cyan-500/20 rounded-full blur-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-4 bg-slate-950">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="mb-4 bg-purple-500/20 border-purple-400/30 text-purple-300">
            <Star className="w-4 h-4 mr-2 inline fill-purple-400" /> Testimonials
          </Badge>
          <h2 className="text-4xl font-black mb-12">
            Loved by Those Who{" "}
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Care Most
            </span>
          </h2>

          <div className="relative">
            <Card className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 border-slate-700">
              <CardContent className="p-10">
                <div className="flex justify-center gap-1 mb-6">
                  {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                    <Star key={i} className="w-6 h-6 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-2xl text-slate-200 mb-8 italic leading-relaxed">
                  "{testimonials[activeTestimonial].quote}"
                </p>
                <div>
                  <p className="text-white font-bold text-lg">{testimonials[activeTestimonial].author}</p>
                  <p className="text-slate-400">{testimonials[activeTestimonial].role}</p>
                </div>
              </CardContent>
            </Card>

            {/* Testimonial Navigation */}
            <div className="flex justify-center gap-3 mt-8">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveTestimonial(i)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    i === activeTestimonial 
                      ? "bg-purple-400 w-8" 
                      : "bg-slate-600 hover:bg-slate-500"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Teaser */}
      <section id="pricing-section" className="py-24 px-4 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="mb-4 bg-green-500/20 border-green-400/30 text-green-300">
            Simple Pricing
          </Badge>
          <h2 className="text-4xl font-black mb-6">
            <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
              $59
            </span>
            /vehicle/month
          </h2>
          <p className="text-xl text-slate-400 mb-8">
            Unlimited rides. Cancel anytime. No setup fees. No hidden costs.
          </p>
          
          {/* Transparency Paragraph */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 mb-10 text-left max-w-3xl mx-auto">
            <p className="text-slate-300 leading-relaxed">
              Our commercial model is intentionally simple and fully transparent. All platform capabilities 
              are included, with no incremental feature fees, no onboarding or implementation charges, and 
              no separate costs for training or ongoing support. There are no long-term contracts or lock-in 
              periods, and no hidden pricing buried in the fine print. What you see is exactly what you get—
              <span className="text-white font-semibold">predictable, all-inclusive pricing</span> designed 
              to align with enterprise procurement standards and eliminate cost uncertainty.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white font-bold px-8 py-6 text-lg rounded-xl"
              onClick={() => navigate("/pricing")}
            >
              View All Plans <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-4 bg-gradient-to-r from-cyan-600 via-purple-600 to-orange-500">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-black mb-6 text-white">
            Every Patient Deserves to Arrive
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Join 500+ healthcare providers who've made transportation one less thing 
            their patients have to worry about.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-white text-purple-600 hover:bg-slate-100 font-bold px-10 py-6 text-lg rounded-xl shadow-2xl"
              onClick={() => navigate("/")}
            >
              Start Your Free Trial
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-white/50 text-white hover:bg-white/10 px-10 py-6 text-lg rounded-xl"
              onClick={() => window.open("tel:+18001234567")}
            >
              <Phone className="mr-2 w-5 h-5" /> Talk to Sales
            </Button>
          </div>
          <p className="mt-8 text-white/70 text-sm">
            No credit card required • 14-day free trial • Setup in minutes
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-slate-950 border-t border-slate-800">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-400 to-purple-500 rounded-xl flex items-center justify-center">
                <Navigation className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-white">There You Are</span>
            </div>
            <p className="text-slate-500 text-sm">
              © 2025 There You Are. Making medical transportation human again.
            </p>
            <div className="flex items-center gap-4">
              <Badge className="bg-green-500/20 text-green-300 border-green-500/30">
                <Shield className="w-3 h-3 mr-1 inline" /> HIPAA Compliant
              </Badge>
            </div>
          </div>
        </div>
      </footer>

      {/* Pricing Modal */}
      {showPricing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowPricing(false)} />
          <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-3xl p-6 md:p-8 w-full max-w-4xl shadow-2xl border border-slate-700 max-h-[90vh] overflow-y-auto">
            {/* Close Button */}
            <button 
              onClick={() => setShowPricing(false)}
              className="absolute top-4 right-4 w-10 h-10 bg-slate-700 hover:bg-slate-600 rounded-full flex items-center justify-center text-white transition-all"
            >
              ✕
            </button>

            {/* 14-Day Trial Badge */}
            <div className="flex justify-center mb-6">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-400/40 rounded-full px-5 py-2">
                <Sparkles className="w-5 h-5 text-amber-400" />
                <span className="text-amber-300 font-bold">14-Day Free Trial</span>
                <span className="text-slate-400">• No credit card required</span>
              </div>
            </div>

            {/* Pricing Header */}
            <div className="text-center mb-8">
              <h2 className="text-5xl md:text-6xl font-black mb-4">
                <span className="text-white">Flat Rate </span>
                <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">$59</span>
                <span className="text-white">/vehicle</span>
              </h2>
              <p className="text-slate-400 text-xl">per month • Unlimited rides • Cancel anytime</p>
            </div>

            {/* Call Center Image */}
            <div className="mb-6 rounded-2xl overflow-hidden max-h-64 md:max-h-80">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/f58f15e8-8750-466f-aafd-74b21bfd3001/images/e06d0b4d6478d055e5d993241690d11dfcb83efa1408e09160d1c1dae75ec285.png"
                alt="There You Are dispatch call center with staff using the platform"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Sales Pitch */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-4 md:p-6">
              <p className="text-base md:text-lg text-slate-200 leading-relaxed">
                Our commercial model is intentionally simple and fully transparent. All platform capabilities 
                are included, with <span className="text-white font-semibold">no incremental feature fees</span>, 
                no onboarding or implementation charges, and no separate costs for training or ongoing support. 
                There are no long-term contracts or lock-in periods, and no hidden pricing buried in the fine print. 
                What you see is exactly what you get—<span className="text-green-400 font-bold">predictable, all-inclusive pricing</span> designed 
                to align with enterprise procurement standards and eliminate cost uncertainty.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center mt-6">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white font-bold px-10 py-6 text-lg rounded-xl"
                onClick={() => {
                  setShowPricing(false);
                  navigate("/");
                }}
              >
                Start 14-Day Free Trial <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-slate-600 text-white hover:bg-slate-700 px-10 py-6 text-lg rounded-xl"
                onClick={() => {
                  setShowPricing(false);
                  setShowChat(true);
                }}
              >
                <MessageSquare className="mr-2 w-5 h-5" /> Chat with Agent
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Chat Modal */}
      {showChat && (
        <div className="fixed bottom-6 right-6 z-50 w-96 max-w-[calc(100vw-3rem)]">
          <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl shadow-2xl border border-slate-700 overflow-hidden">
            {/* Chat Header */}
            <div className="bg-gradient-to-r from-cyan-600 to-blue-600 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-white font-semibold">Chat with Us</p>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                    <p className="text-cyan-100 text-sm">Online now</p>
                  </div>
                </div>
              </div>
              <button 
                onClick={() => setShowChat(false)}
                className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-all"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Chat Messages */}
            <div className="h-80 overflow-y-auto p-4 space-y-4 bg-slate-900/50">
              {chatMessages.map((msg, i) => (
                <div key={i} className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    msg.from === 'user' 
                      ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white' 
                      : 'bg-slate-700 text-slate-200'
                  }`}>
                    {msg.from === 'agent' && (
                      <p className="text-cyan-400 text-xs font-semibold mb-1">Sarah - Sales Agent</p>
                    )}
                    <p className="text-sm">{msg.text}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Chat Input */}
            <div className="p-4 border-t border-slate-700 bg-slate-800">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                  placeholder="Type your message..."
                  className="flex-1 bg-slate-700 border border-slate-600 rounded-xl px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:border-cyan-500"
                />
                <button 
                  onClick={sendChatMessage}
                  className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center text-white hover:from-cyan-400 hover:to-blue-400 transition-all"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating Chat Button (when chat is closed) */}
      {!showChat && (
        <button
          onClick={() => setShowChat(true)}
          className="fixed bottom-6 right-6 z-40 w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full shadow-2xl shadow-cyan-500/30 flex items-center justify-center text-white hover:from-cyan-400 hover:to-blue-500 transition-all group"
        >
          <MessageSquare className="w-7 h-7 group-hover:scale-110 transition-transform" />
          <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-slate-900 animate-pulse" />
        </button>
      )}
    </div>
  );
}
